import { CloudUpload, FileText } from "lucide-react";
import {
  Group,
  Space,
  Button,
  Text,
  Container,
  Center,
  Select,
  Loader,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import React, { useEffect, useRef, useState } from "react";
import { DEFAULT_EMPLOYEE_CONFIG } from "../utils/constants";
import { HttpClientError } from "../../../providers/HttpClient";
import useEmployerImportConfigurationApiService from "../services/api/EmployeeImportConfigurationApiService";
import AuthProfile from "../../auth/services/model/AuthProfile";

type EmployeeUploadFileProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  onDone: (value: { [key: string]: string[] }) => void;
  auth: AuthProfile;
};

const EmployeeUploadFile: React.FC<EmployeeUploadFileProps> = ({
  form,
  onDone,
  auth,
}) => {
  const openRef = useRef<() => void>(null);

  const [configOptions, setConfigOptions] = useState<string[]>([]);
  const [selectConfigOptions, setSelectConfigOptions] = useState<string>(
    form.values.configurationToUse
  );

  const { isLoading, isError, error, data } =
    useEmployerImportConfigurationApiService(auth.employer.id);

  useEffect(() => {
    if (data) {
      const tempOptions: string[] = [];
      data.forEach((element) => {
        if (!tempOptions.includes(element.name)) {
          tempOptions.push(element.name);
        }
      });

      setConfigOptions(tempOptions);
    }
  }, [data]);

  return (
    <div className="flex justify-center items-center  border-dashed border-2 mt-10 border-gray-300">
      <div className="py-8 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {form.values.file ? (
            <>
              <Space h="xl" />
              <Space h="xl" />
              <Space h="xl" />
              <Space h="xl" />
              <Space h="xl" />
              <div style={{ pointerEvents: "none" }}>
                <Group position="center">
                  <FileText size={50} />
                </Group>
                <Space h="md" />
                <Text
                  align="center"
                  weight={700}
                  size="lg"
                  mt="xl"
                  //   sx={{ color: getActiveColor(status, theme) }}
                >
                  {form.values.file.name}
                </Text>
              </div>
              <Space h="xl" />
              <Center>
                <Container size={500}>
                  <Button
                    type="button"
                    onClick={() => {
                      form.setFieldValue("file", undefined);
                    }}
                    variant="outline"
                    color="jem-theme"
                    className="inline-flex"
                  >
                    Remove File
                  </Button>
                  <Space h="xl" />
                </Container>
              </Center>
              <Select
                placeholder="Select previous Config"
                style={{
                  width: "100%",
                }}
                data={configOptions.map((it) => ({
                  value: it,
                  label: it,
                }))}
                error={isError && HttpClientError(error)}
                searchable
                clearable
                value={form.values.configurationToUse}
                onChange={(value) => {
                  setSelectConfigOptions((current) => value || current);
                  form.setFieldValue("configurationToUse", value);
                }}
                disabled={isError || !data}
                rightSection={isLoading ? <Loader size={16} /> : null}
              />
            </>
          ) : (
            <>
              <Dropzone
                openRef={openRef}
                className="border-0	"
                onDrop={(files) => {
                  form.setFieldValue("file", files[0]);

                  new Response(files[0]).text().then((res) => {
                    const headers = res
                      .slice(0, res.indexOf("\r\n"))
                      .split(",")
                      .filter(Boolean);
                    const rows = res
                      .replace(res.slice(0, res.indexOf("\r\n")), "")
                      .split("\r\n");
                    let results: { [key: string]: string[] } = {};
                    form.setFieldValue(
                      "columnHeaders",
                      headers.map((name: string, index: number) => {
                        let isFound;

                        if (selectConfigOptions && data) {
                          form.setFieldValue(
                            "configurationName",
                            selectConfigOptions
                          );

                          isFound = data
                            .filter((it) =>
                              Object.is(it.name, selectConfigOptions)
                            )
                            .find((it) => Object.is(name, it.csv_field));

                          const row: string[] = [];
                          for (let i = 0; i < 3; i += 1) {
                            const result = rows.filter(Boolean)[i].split(",")[
                              index
                            ];
                            row.push(result || "-");
                          }

                          results = {
                            ...results,
                            [name]: row,
                          };

                          return {
                            name,
                            relatedColumn: isFound
                              ? isFound.smartwage_field
                              : "",
                            isTag: isFound?.is_tag || false,
                          };
                        }

                        isFound = DEFAULT_EMPLOYEE_CONFIG.find((it) =>
                          Object.is(name, it.value)
                        );

                        const row: string[] = [];
                        for (let i = 0; i < 3; i += 1) {
                          const result = rows.filter(Boolean)[i].split(",")[
                            index
                          ];
                          row.push(result || "-");
                        }

                        results = {
                          ...results,
                          [name]: row,
                        };

                        return {
                          name,
                          relatedColumn: isFound ? isFound.value : "",
                          isTag: false,
                        };
                      })
                    );

                    if (results) {
                      onDone(results);
                    }
                  });
                }}
                radius="md"
                accept={[MIME_TYPES.csv]}
                maxSize={30 * 1024 ** 2}
              >
                {() => (
                  <div style={{ pointerEvents: "none" }}>
                    <Group position="center">
                      <CloudUpload size={50} />
                    </Group>
                    <Text
                      align="center"
                      weight={700}
                      size="lg"
                      mt="xl"
                      //   sx={{ color: getActiveColor(status, theme) }}
                    >
                      Upload file
                    </Text>
                    <Text align="center" size="sm" mt="xs" color="dimmed">
                      Drag and drop a CSV file here to upload employee data or
                      select a file from your computer to upload.
                    </Text>
                  </div>
                )}
              </Dropzone>
              <Space h="xl" />
              <Space h="xl" />
              <Select
                placeholder="Select previous Config"
                style={{
                  width: "100%",
                }}
                data={configOptions.map((it) => ({
                  value: it,
                  label: it,
                }))}
                error={isError && HttpClientError(error)}
                searchable
                clearable
                value={form.values.configurationToUse}
                onChange={(value) => {
                  setSelectConfigOptions((current) => value || current);
                  form.setFieldValue("configurationToUse", value);
                }}
                disabled={isError || !data}
                rightSection={isLoading ? <Loader size={16} /> : null}
              />

              <Space h="xl" />
              <Space h="xl" />
              <Space h="xl" />

              <Center>
                <Container size={500}>
                  <Button
                    type="button"
                    color="jem-theme"
                    onClick={() => {
                      if (openRef && openRef.current) {
                        openRef.current();
                      }
                    }}
                    variant="filled"
                    className="inline-flex"
                  >
                    Select CSV
                  </Button>
                  <Space h="xl" />
                </Container>
              </Center>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeUploadFile;
